import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';

import {JsonLd, buildProductJsonLd, PageCta, PageHero, PageShell, PageTitle, Section} from 'components';
import Icon from 'components/v3/Icon';
import {btnPrimaryLg} from 'components/v3/classes';
import {URLS} from 'constants/routes';

const PHOTO_PATHS = [
  'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
];
const COG_PATHS = [
  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
];
const GRID_PATHS = [
  'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
];

interface Feature {
  descKey: string;
  iconPaths: string[];
  titleKey: string;
}

const renderFeature = (feature: Feature, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6 lg:p-7" key={feature.titleKey}>
    <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center mb-5">
      <Icon className="w-6 h-6 text-amber-600" paths={feature.iconPaths} />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t(feature.titleKey)}</h3>
    <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t(feature.descKey)}</p>
  </div>
);

interface UseCase {
  descKey: string;
  titleKey: string;
}

const renderUseCase = (useCase: UseCase, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6" key={useCase.titleKey}>
    <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 mb-5" />
    <h3 className="text-base font-semibold text-slate-900 mb-2">{t(useCase.titleKey)}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{t(useCase.descKey)}</p>
  </div>
);

const ImageFusion: FC = () => {
  const {t} = useTranslation('products');

  const features: Feature[] = [
    {descKey: 'fusion.features.scene.desc', iconPaths: PHOTO_PATHS, titleKey: 'fusion.features.scene.title'},
    {descKey: 'fusion.features.style.desc', iconPaths: COG_PATHS, titleKey: 'fusion.features.style.title'},
    {descKey: 'fusion.features.batch.desc', iconPaths: GRID_PATHS, titleKey: 'fusion.features.batch.title'},
  ];

  const useCases: UseCase[] = [
    {descKey: 'fusion.useCases.amazonMain.desc', titleKey: 'fusion.useCases.amazonMain.title'},
    {descKey: 'fusion.useCases.independentSiteAd.desc', titleKey: 'fusion.useCases.independentSiteAd.title'},
    {descKey: 'fusion.useCases.socialCreative.desc', titleKey: 'fusion.useCases.socialCreative.title'},
    {descKey: 'fusion.useCases.multiPlatform.desc', titleKey: 'fusion.useCases.multiPlatform.title'},
  ];

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/product-image"
        description={t('fusion.seo.description')}
        ogDescription={t('fusion.seo.ogDescription')}
        title={t('fusion.seo.title')}
      />
      <JsonLd
        data={buildProductJsonLd({
          homeLabel: t('common:nav.homeAria'),
          path: '/product-image',
          productDescription: t('fusion.seo.description'),
          productName: t('fusion.seo.title'),
        })}
      />
      <PageHero
        actions={
          <a className={btnPrimaryLg} href={URLS.fusionWorkspace} rel="noreferrer" target="_blank">
            {t('fusion.cta')}
          </a>
        }
        align="center"
        badge={t('fusion.hero.badge')}
        description={
          <>
            {t('fusion.descLine1')}
            <br />
            {t('fusion.descLine2')}
          </>
        }
        title={t('fusion.title')}
        variant="light"
      />
      <Section title={t('fusion.capabilitiesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{features.map((f) => renderFeature(f, t))}</div>
      </Section>
      <Section bg="muted" title={t('fusion.useCasesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{useCases.map((u) => renderUseCase(u, t))}</div>
      </Section>
      <PageCta
        actions={
          <a className={btnPrimaryLg} href={URLS.fusionWorkspace} rel="noreferrer" target="_blank">
            {t('fusion.cta')}
          </a>
        }
        title={t('fusion.ctaTitle')}
      />
    </PageShell>
  );
};

export default ImageFusion;
